import Phaser from 'phaser';
import { ObjectInfoPanel, ObjectData } from './ObjectInfoPanel';
import type { LoadedLevel } from '../core/LevelLoader';
import { Entity } from '../entities/base/Entity';
import { HealthBehavior } from '../entities/behaviors/HealthBehavior';
import { LayerDepths } from '../constants/enums';

/**
 * ObjectInspectorOverlay - Visualizes bounding boxes for ALL objects (not just physics bodies)
 *
 * Toggle with SHIFT+F3 to show/hide bounding boxes of:
 * - Decorations (sprites, images)
 * - TileSprites
 * - Tilemap tiles
 * - Any game object in level containers
 *
 * Click on any object to see detailed info in a panel.
 *
 * This complements CollisionDebugOverlay (SHIFT+F1) which only shows physics bodies.
 */

const COLORS = {
  normal: 0x00ffff,    // Cyan for normal objects
  selected: 0xff00ff,  // Magenta for selected object
  text: '#00ffff',
};

export class ObjectInspectorOverlay {
  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;
  private enabled: boolean = false;
  private loadedLevel: LoadedLevel;
  private selectedObject: Phaser.GameObjects.GameObject | null = null;
  private infoPanel: ObjectInfoPanel;
  private statusText: Phaser.GameObjects.Text;

  // Cache for layer names
  private objectLayerMap: Map<Phaser.GameObjects.GameObject, string> = new Map();

  // For cycling through overlapping objects
  private objectsAtClickPoint: Phaser.GameObjects.GameObject[] = [];
  private currentObjectIndex: number = 0;
  private lastClickX: number = 0;
  private lastClickY: number = 0;

  constructor(scene: Phaser.Scene, loadedLevel: LoadedLevel) {
    this.scene = scene;
    this.loadedLevel = loadedLevel;

    // Create graphics for drawing bounding boxes
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(LayerDepths.DebugGraphics);
    this.graphics.setVisible(false);

    // Create info panel
    this.infoPanel = new ObjectInfoPanel(scene);

    // Create status text
    this.statusText = scene.add.text(16, scene.scale.height - 40, '', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: COLORS.text,
      backgroundColor: '#000000cc',
      padding: { x: 10, y: 6 },
    });
    this.statusText.setScrollFactor(0);
    this.statusText.setDepth(LayerDepths.DebugGraphics);
    this.statusText.setVisible(false);

    // Setup key handler for Ctrl+Alt
    this.setupKeyHandler();

    // Setup click handler
    this.setupClickHandler();

    // Build layer map on construction
    this.buildObjectLayerMap();
  }

  /**
   * Setup keydown listener for SHIFT+F3 toggle
   */
  private setupKeyHandler(): void {
    this.scene.input.keyboard!.on('keydown-F3', (event: KeyboardEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
        this.toggle();
      }
    });
  }

  /**
   * Setup click handler for object selection
   */
  private setupClickHandler(): void {
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled) return;

      // Ignore if clicking on the info panel
      if (this.infoPanel.containsPoint(pointer.x, pointer.y)) {
        return;
      }

      // Get world coordinates
      const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);

      // Check if clicking at same position (within 5px tolerance)
      const samePosition =
        Math.abs(world.x - this.lastClickX) < 5 &&
        Math.abs(world.y - this.lastClickY) < 5;

      if (samePosition && this.objectsAtClickPoint.length > 1) {
        // Cycle to next object at this point
        this.currentObjectIndex = (this.currentObjectIndex + 1) % this.objectsAtClickPoint.length;
        const obj = this.objectsAtClickPoint[this.currentObjectIndex];
        this.selectObject(obj, this.currentObjectIndex, this.objectsAtClickPoint.length);
      } else {
        // New click position - find all objects here
        this.lastClickX = world.x;
        this.lastClickY = world.y;
        this.objectsAtClickPoint = this.findAllObjectsAtPoint(world.x, world.y);
        this.currentObjectIndex = 0;

        if (this.objectsAtClickPoint.length > 0) {
          this.selectObject(
            this.objectsAtClickPoint[0],
            0,
            this.objectsAtClickPoint.length
          );
        } else {
          this.deselectObject();
        }
      }
    });
  }

  /**
   * Toggle visibility of the overlay
   */
  toggle(): void {
    this.enabled = !this.enabled;
    this.graphics.setVisible(this.enabled);
    this.statusText.setVisible(this.enabled);

    if (this.enabled) {
      console.log('[Debug] Object Inspector ENABLED - Press SHIFT+F3 to hide, click to inspect');
      // Rebuild map in case level changed
      this.buildObjectLayerMap();
    } else {
      console.log('[Debug] Object Inspector DISABLED');
      this.graphics.clear();
      this.deselectObject();
    }
  }

  /**
   * Returns whether the overlay is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Build a map from objects to their layer names
   */
  private buildObjectLayerMap(): void {
    this.objectLayerMap.clear();

    for (const [layerName, container] of this.loadedLevel.containers.layers) {
      container.each((child: Phaser.GameObjects.GameObject) => {
        this.objectLayerMap.set(child, layerName);
      });
    }

    // Also map collectibles and npcs containers
    this.loadedLevel.containers.collectibles.each((child: Phaser.GameObjects.GameObject) => {
      this.objectLayerMap.set(child, 'Collectibles');
    });
    this.loadedLevel.containers.npcs.each((child: Phaser.GameObjects.GameObject) => {
      this.objectLayerMap.set(child, 'NPCs');
    });
  }

  /**
   * Collect all visible objects within the camera viewport
   */
  private collectVisibleObjects(): Phaser.GameObjects.GameObject[] {
    const objects: Phaser.GameObjects.GameObject[] = [];
    const camera = this.scene.cameras.main;

    // Get camera bounds in world space
    const viewLeft = camera.scrollX - 100;
    const viewRight = camera.scrollX + camera.width + 100;
    const viewTop = camera.scrollY - 100;
    const viewBottom = camera.scrollY + camera.height + 100;

    // Collect from all layer containers
    for (const [, container] of this.loadedLevel.containers.layers) {
      container.each((child: Phaser.GameObjects.GameObject) => {
        if (this.isInViewport(child, viewLeft, viewRight, viewTop, viewBottom)) {
          objects.push(child);
        }
      });
    }

    // Also include collectibles and NPCs
    this.loadedLevel.containers.collectibles.each((child: Phaser.GameObjects.GameObject) => {
      if (this.isInViewport(child, viewLeft, viewRight, viewTop, viewBottom)) {
        objects.push(child);
      }
    });

    this.loadedLevel.containers.npcs.each((child: Phaser.GameObjects.GameObject) => {
      if (this.isInViewport(child, viewLeft, viewRight, viewTop, viewBottom)) {
        objects.push(child);
      }
    });

    return objects;
  }

  /**
   * Check if an object is within the viewport
   */
  private isInViewport(
    obj: Phaser.GameObjects.GameObject,
    viewLeft: number,
    viewRight: number,
    viewTop: number,
    viewBottom: number
  ): boolean {
    const bounds = this.getObjectBounds(obj);
    if (!bounds) return false;

    return (
      bounds.right >= viewLeft &&
      bounds.left <= viewRight &&
      bounds.bottom >= viewTop &&
      bounds.top <= viewBottom
    );
  }

  /**
   * Get the bounds of an object
   */
  private getObjectBounds(obj: Phaser.GameObjects.GameObject): {
    left: number;
    right: number;
    top: number;
    bottom: number;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null {
    // Handle different object types
    if ('getBounds' in obj && typeof obj.getBounds === 'function') {
      const bounds = (obj as Phaser.GameObjects.Components.GetBounds).getBounds();
      return {
        left: bounds.x,
        right: bounds.x + bounds.width,
        top: bounds.y,
        bottom: bounds.y + bounds.height,
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height,
      };
    }

    // Fallback for objects with x, y, displayWidth, displayHeight
    const sprite = obj as Phaser.GameObjects.Sprite;
    if (sprite.x !== undefined && sprite.y !== undefined) {
      const width = sprite.displayWidth ?? 32;
      const height = sprite.displayHeight ?? 32;
      const originX = sprite.originX ?? 0.5;
      const originY = sprite.originY ?? 0.5;

      const left = sprite.x - width * originX;
      const top = sprite.y - height * originY;

      return {
        left,
        right: left + width,
        top,
        bottom: top + height,
        x: left,
        y: top,
        width,
        height,
      };
    }

    return null;
  }

  /**
   * Draw a bounding box around an object
   */
  private drawBoundingBox(obj: Phaser.GameObjects.GameObject, isSelected: boolean): void {
    const bounds = this.getObjectBounds(obj);
    if (!bounds) return;

    const color = isSelected ? COLORS.selected : COLORS.normal;
    const alpha = isSelected ? 0.4 : 0.15;
    const lineWidth = isSelected ? 3 : 1;

    // Fill
    this.graphics.fillStyle(color, alpha);
    this.graphics.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);

    // Stroke
    this.graphics.lineStyle(lineWidth, color, isSelected ? 1 : 0.6);
    this.graphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);

    // Draw center marker for selected
    if (isSelected) {
      const centerX = bounds.x + bounds.width / 2;
      const centerY = bounds.y + bounds.height / 2;
      const markerSize = Math.min(10, bounds.width * 0.1, bounds.height * 0.1);

      this.graphics.lineStyle(2, color, 0.8);
      this.graphics.beginPath();
      this.graphics.moveTo(centerX - markerSize, centerY);
      this.graphics.lineTo(centerX + markerSize, centerY);
      this.graphics.moveTo(centerX, centerY - markerSize);
      this.graphics.lineTo(centerX, centerY + markerSize);
      this.graphics.strokePath();
    }
  }

  /**
   * Find the topmost object at a world point
   */
  private findObjectAtPoint(worldX: number, worldY: number): Phaser.GameObjects.GameObject | null {
    const objects = this.findAllObjectsAtPoint(worldX, worldY);
    return objects.length > 0 ? objects[0] : null;
  }

  /**
   * Find ALL objects at a world point, sorted by depth (highest first)
   */
  private findAllObjectsAtPoint(worldX: number, worldY: number): Phaser.GameObjects.GameObject[] {
    const allObjects = this.collectVisibleObjects();
    const objectsAtPoint: Phaser.GameObjects.GameObject[] = [];

    for (const obj of allObjects) {
      if (this.containsPoint(obj, worldX, worldY)) {
        objectsAtPoint.push(obj);
      }
    }

    // Sort by effective depth (higher depth = on top, index 0)
    objectsAtPoint.sort((a, b) => this.getEffectiveDepth(b) - this.getEffectiveDepth(a));

    return objectsAtPoint;
  }

  /**
   * Check if an object contains a world point
   */
  private containsPoint(obj: Phaser.GameObjects.GameObject, worldX: number, worldY: number): boolean {
    const bounds = this.getObjectBounds(obj);
    if (!bounds) return false;

    return (
      worldX >= bounds.left &&
      worldX <= bounds.right &&
      worldY >= bounds.top &&
      worldY <= bounds.bottom
    );
  }

  /**
   * Get effective depth considering container hierarchy
   */
  private getEffectiveDepth(obj: Phaser.GameObjects.GameObject): number {
    const sprite = obj as Phaser.GameObjects.Sprite;
    let depth = sprite.depth ?? 0;

    // Add parent container depth
    if (sprite.parentContainer) {
      depth += sprite.parentContainer.depth * 1000;
    }

    return depth;
  }

  /**
   * Select an object and show its info
   */
  private selectObject(
    obj: Phaser.GameObjects.GameObject,
    index: number = 0,
    total: number = 1
  ): void {
    this.selectedObject = obj;
    const data = this.extractObjectData(obj);

    // Add stack info if multiple objects at this point
    if (total > 1) {
      data.stackInfo = `${index + 1}/${total}`;
    }

    this.infoPanel.show(data);
    const stackMsg = total > 1 ? ` (${index + 1}/${total} - click again to cycle)` : '';
    console.log('[Debug] Selected object:', data.name, 'UID:', data.uid, stackMsg);
  }

  /**
   * Deselect current object
   */
  private deselectObject(): void {
    this.selectedObject = null;
    this.infoPanel.hide();
  }

  /**
   * Extract all relevant data from an object
   */
  private extractObjectData(obj: Phaser.GameObjects.GameObject): ObjectData {
    const sprite = obj as Phaser.GameObjects.Sprite;
    const bounds = this.getObjectBounds(obj);

    // Get layer name
    const layerName = this.objectLayerMap.get(obj) ?? 'Unknown';

    // Get object name/type
    const textureName = sprite.texture?.key ?? null;
    const typeName = obj.constructor.name;
    const name = textureName ?? typeName;

    // Extract all custom data
    const customData: Record<string, unknown> = {};
    if (obj.data) {
      const values = obj.data.getAll();
      for (const [key, value] of Object.entries(values)) {
        customData[key] = value;
      }
    }

    // Check for tilemap info (stored as custom data)
    const tileCol = customData['tileCol'] as number | undefined;
    const tileRow = customData['tileRow'] as number | undefined;
    const tileIndex = customData['tileIndex'] as number | undefined;

    // Extract HP if entity has HealthBehavior
    let hp: number | undefined;
    let maxHp: number | undefined;

    if (obj instanceof Entity) {
      const healthBehavior = obj.getBehavior(HealthBehavior);
      if (healthBehavior) {
        hp = healthBehavior.hp;
        maxHp = healthBehavior.maxHp;
      }
    }

    return {
      uid: (customData['uid'] as number) ?? null,
      name,
      type: typeName,
      layerName,

      x: sprite.x ?? 0,
      y: sprite.y ?? 0,
      width: bounds?.width ?? 0,
      height: bounds?.height ?? 0,
      originX: sprite.originX ?? 0.5,
      originY: sprite.originY ?? 0.5,
      angle: sprite.angle ?? 0,
      scaleX: sprite.scaleX ?? 1,
      scaleY: sprite.scaleY ?? 1,
      flipX: sprite.flipX ?? false,
      flipY: sprite.flipY ?? false,
      depth: sprite.depth ?? 0,

      texture: textureName,
      frame: sprite.frame?.name ?? null,

      tileCol,
      tileRow,
      tileIndex,

      customData,

      hp,
      maxHp,
    };
  }

  /**
   * Update - call every frame to redraw bounding boxes
   */
  update(): void {
    if (!this.enabled) return;

    // Clear previous graphics
    this.graphics.clear();

    // Collect visible objects
    const objects = this.collectVisibleObjects();

    // Draw bounding boxes
    for (const obj of objects) {
      const isSelected = obj === this.selectedObject;
      this.drawBoundingBox(obj, isSelected);
    }

    // Update status text
    this.statusText.setText(
      `[OBJECT INSPECTOR] SHIFT+F3 to hide | Objects: ${objects.length} | Click to inspect`
    );
  }

  /**
   * Destroy the overlay and clean up
   */
  destroy(): void {
    this.graphics.destroy();
    this.statusText.destroy();
    this.infoPanel.destroy();
    this.objectLayerMap.clear();
  }
}
